import { useUser } from "@context/UserContext";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  ConfirmDialog,
  FieldError,
} from "@scribenote/brian-design-system";
import { useRemoveUser } from "@users/userQueries";
import { useState } from "react";

/** Inert on the signed-in admin's own row, mirroring brian's 409 on a self-delete. */
export const RemoveUserButton = ({
  user,
  sessionKey,
}: {
  user: { id: string; displayName: string };
  sessionKey: string;
}) => {
  const { session } = useUser();
  const [confirming, setConfirming] = useState(false);
  const removeUser = useRemoveUser(sessionKey);
  const isSelf = session?.userUuid === user.id;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setConfirming(true)}
        disabled={isSelf}
        title={isSelf ? "You can’t remove yourself." : undefined}
        className="text-ink-stopped"
        data-testid={`admin-user-remove-${user.id}`}
      >
        <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
        Remove
      </Button>

      <ConfirmDialog
        open={confirming}
        title={`Remove ${user.displayName}?`}
        confirmLabel="Remove"
        destructive
        busy={removeUser.isPending}
        onCancel={() => {
          removeUser.reset();
          setConfirming(false);
        }}
        onConfirm={() =>
          removeUser.mutate(user.id, {
            onSuccess: () => setConfirming(false),
          })
        }
        confirmTestId={`admin-user-remove-${user.id}-confirm`}
        cancelTestId={`admin-user-remove-${user.id}-cancel`}
      >
        <p className="text-sm text-ink-body">
          They&apos;ll be signed out and won&apos;t be able to sign in. This
          can&apos;t be undone.
        </p>
        {removeUser.isError && (
          <FieldError className="mt-2">
            Couldn’t remove {user.displayName}. Try again.
          </FieldError>
        )}
      </ConfirmDialog>
    </>
  );
};
